import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  DeleteIcon,
  EditIcon,
  Flex,
  H2,
  Input,
  Span,
  SelectDropdown,
  Line,
} from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { getFormattedDate } from '../../lib/utils/helpers'
import type {
  CampaignListPagination,
  GetPromotionalReferralCodesParams,
  PromotionalReferralCodeListItem,
} from '../../services/campaign-service'
import './campaigns-list.scss'

const campaignListSort: {
  text: string
  value: NonNullable<GetPromotionalReferralCodesParams['sortBy']>
}[] = [
  { text: 'Newest to oldest', value: 'NEWEST_TO_OLDEST' },
  { text: 'Oldest to newest', value: 'OLDEST_TO_NEWEST' },
]

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—'
  try {
    return getFormattedDate(new Date(iso), 'D MMM YYYY [at] h:mm A')
  } catch {
    return iso
  }
}

const formatRuleSummary = (rules: PromotionalReferralCodeListItem['rules']) => {
  if (!rules || typeof rules !== 'object') return '—'
  if (rules.waive_dealing_charges === true) return 'Waive dealing charges'
  const s = JSON.stringify(rules)
  return s.length > 64 ? `${s.slice(0, 61)}...` : s
}

type CampaignsListPresenterProps = {
  campaigns: PromotionalReferralCodeListItem[]
  isLoading: boolean
  searchParams: GetPromotionalReferralCodesParams
  handleSearchParams: (params: { name: string; value: string }) => void
  resetSearchParams: () => void
  pagination: CampaignListPagination
  handleCurrentPage: (currentPage: number) => void
  currentPage: number
  onEditCampaign: (row: PromotionalReferralCodeListItem) => void
  onDeleteCampaign: (row: PromotionalReferralCodeListItem) => void
}

function CampaignsListPresenter({
  campaigns,
  isLoading,
  searchParams,
  handleSearchParams,
  resetSearchParams,
  pagination,
  handleCurrentPage,
  currentPage,
  onEditCampaign,
  onDeleteCampaign,
}: CampaignsListPresenterProps) {
  const { breakpoint } = useBreakpoint()
  const navigate = useNavigate()

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Campaigns' />

      <div className='campaigns-list-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          maxLength={128}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search by code or description'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='campaignsListSortBy'
            formId=''
            list={campaignListSort}
            placeholder='Sort'
            value={searchParams.sortBy || 'NEWEST_TO_OLDEST'}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='campaigns-list-sort-select'
          />
          <Button
            handleClick={() => navigate('/campaigns/new')}
            variant='primary-btn'
            text='New campaign'
            classes={['mr-8']}
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>

      <Line />

      {isLoading ? (
        <Flex variant='column-center' classes={['mt-12']}>
          <Span classes={['center']} variant='thin' text='Loading...' />
        </Flex>
      ) : campaigns && campaigns.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='campaigns-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Active</th>
                    <th>Rule</th>
                    <th>Valid from</th>
                    <th>Valid until</th>
                    <th>Max redemptions</th>
                    <th>Created</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((row) => (
                    <tr key={row.id}>
                      <td>{row.code || '—'}</td>
                      <td>{row.description || '—'}</td>
                      <td>
                        {row.is_active === true ? 'Yes' : row.is_active === false ? 'No' : '—'}
                      </td>
                      <td>{formatRuleSummary(row.rules)}</td>
                      <td>{formatDate(row.valid_from)}</td>
                      <td>{formatDate(row.valid_until)}</td>
                      <td>
                        {row.max_redemptions !== null && row.max_redemptions !== undefined
                          ? String(row.max_redemptions)
                          : 'Unlimited'}
                      </td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        <EditIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() => onEditCampaign(row)}
                          dataTestId={`campaign-list-edit-icon-${row.id}`}
                        />
                      </td>
                      <td>
                        <DeleteIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='19px'
                          onClick={() => onDeleteCampaign(row)}
                          dataTestId={`campaign-list-delete-icon-${row.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <Flex variant='column-center' classes={['mt-12']}>
          <Span classes={['center']} variant='thin' text='No campaigns found' />
        </Flex>
      )}
    </Flex>
  )
}

export { CampaignsListPresenter }
